import {FC, useEffect} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import clsx from 'clsx';

import {useDispatch, useSelector} from '~/store/hooks';
import Button from '~/common/Button';
import Input from '~/common/Input';
import Textarea from '~/common/Form/Textarea';
import Alert, {AlertTypes} from '~/common/display/Alert';
import {validateEmail, validateProto, validateUrl} from '~/utils/validate';
import {getCaptchaValue, initCaptcha} from '~/utils/captcha';
import {formatUserName} from '~/utils/name';
import {useErrorAutoHide} from '~/hooks/useErrorAutoHide';
import {
  Modals,
  REMOVAL_REQUEST_CAPTCHA_ID,
  REMOVAL_REQUEST_CAPTCHA_NAMESPACE,
  SERVER_ERROR,
} from '~/modals/constants';

import s from '../styles.module.scss';

export interface RemovalRequestFormValues {
  name?: string;
  email?: string;
  url: string;
  reason: string;
  captcha?: string;
}

interface RemovalRequestFormProps {
  isFullHD: boolean;
}

const RemovalRequestForm: FC<RemovalRequestFormProps> = ({isFullHD}) => {
  const dispatch = useDispatch();
  const {error, errorCaptcha} = useSelector((state) => state.removalRequest);
  const {isGuest, data} = useSelector((state) => state.profile);
  const serverError = useErrorAutoHide(error);

  // populate user details in form
  const removalRequestDefaultValues: RemovalRequestFormValues = {
    name: formatUserName(data),
    email: data ? data.email : '',
    url: '',
    reason: '',
    captcha: '',
  };

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    getValues,
    formState: {errors, isValid, isSubmitting},
  } = useForm<RemovalRequestFormValues>({
    defaultValues: removalRequestDefaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    dispatch.removalRequest.setRemovalRequestDefaultState();

    if (isGuest) {
      initCaptcha(`#${REMOVAL_REQUEST_CAPTCHA_ID}`, REMOVAL_REQUEST_CAPTCHA_NAMESPACE, () => {
        setValue('captcha', '');
      });
    }
  }, []);

  useEffect(() => {
    if (errorCaptcha) {
      setError('captcha', {type: 'server'});
    }
  }, [errorCaptcha]);

  useEffect(() => {
    if (serverError) {
      initCaptcha(`#${REMOVAL_REQUEST_CAPTCHA_ID}`, REMOVAL_REQUEST_CAPTCHA_NAMESPACE, () => {
        setValue('captcha', '');
      });
    }
  }, [serverError]);

  const onSubmit: SubmitHandler<RemovalRequestFormValues> = async (
    formValues: RemovalRequestFormValues
  ) => dispatch.removalRequest.removalRequestSave(formValues);

  const hideModal = () => {
    dispatch.modals.hideModal(Modals.RemovalRequestModal);
    dispatch.removalRequest.setRemovalRequestDefaultState();
    reset();
  };

  return (
    <form className={s['removal-request-form']} onSubmit={handleSubmit(onSubmit)}>
      {isGuest && (
        <div
          className={clsx(s['removal-request-row'], {[s['removal-request-row--fhd']]: isFullHD})}
        >
          <div className={s['removal-request-row-50']}>
            <Input
              label="Your Name"
              required
              type="text"
              className={s['removal-request-input']}
              error={errors.name?.message ?? ''}
              minLength={4}
              maxLength={60}
              placeholder="Enter your name"
              {...register('name', {
                pattern: {
                  message: 'Please type correct name (only letters and spaces)',
                  value: /^[a-zA-Z\s]*$/,
                },
              })}
            />
          </div>
          <div className={s['removal-request-row-50']}>
            <Input
              label="Your Email Address"
              required
              type="email"
              className={s['removal-request-input']}
              error={errors.email?.message ?? ''}
              minLength={4}
              maxLength={200}
              placeholder="Enter your email address"
              {...register('email', {
                required: true,
                validate: (value) => {
                  if (validateEmail(value)) {
                    return true;
                  }
                  return 'Please type valid email address';
                },
              })}
            />
          </div>
        </div>
      )}

      <div className={clsx(s['removal-request-row'], {[s['removal-request-row--fhd']]: isFullHD})}>
        <Input
          label="URL of Content"
          required
          type="url"
          className={s['removal-request-input']}
          error={errors.url?.message ?? ''}
          minLength={4}
          maxLength={200}
          placeholder="Enter URL"
          {...register('url', {
            required: true,
            validate: (value) => {
              if (validateUrl(value, false)) {
                return true;
              }
              return 'Please type valid URL';
            },
          })}
          onBlur={(e) => {
            if (!errors.url) {
              const url = getValues('url');

              if (!validateProto(url)) {
                setValue('url', `https://${(url ?? '').slice(0, 192)}`);
                e.target.dispatchEvent(new Event('input'));
              }

              return register('url').onBlur(e);
            }
          }}
        />
      </div>

      <div className={clsx(s['removal-request-row'], {[s['removal-request-row--fhd']]: isFullHD})}>
        <Textarea
          className={s['removal-request-input']}
          label="Reason for Removal"
          required
          placeholder="Type details here"
          minRows={4}
          minLength={10}
          maxLength={2048}
          error={errors.reason?.message ?? ''}
          {...register('reason', {
            required: 'Please type valid text',
            validate: (value: string) => {
              if (value && value.length < 10) {
                return 'Please type at least of 10 characters.';
              }
            },
          })}
        />
      </div>

      {isGuest && (
        <div className={s['removal-request-row-captcha']}>
          <Controller
            name="captcha"
            control={control}
            rules={{required: true}}
            render={({field}) => (
              <div
                id={REMOVAL_REQUEST_CAPTCHA_ID}
                className="visual-captcha-react"
                onClick={(event) => {
                  const eventTarget = event.target as HTMLDivElement;

                  // Hack to check if click on captcha image
                  const targetValue = eventTarget.classList.contains('visualCaptcha-img');
                  const childValue = eventTarget.querySelector('.visualCaptcha-img');

                  if (!targetValue && !childValue) {
                    return;
                  }

                  // try to get captcha value
                  field.onChange(getCaptchaValue(`#${REMOVAL_REQUEST_CAPTCHA_ID}`));
                }}
              />
            )}
          />

          {errors.captcha && (
            <div className={s['captcha-error-message']}>
              {errors.captcha.type === 'server' ? 'Wrong captcha!' : 'Captcha is required!'}
            </div>
          )}
        </div>
      )}

      {serverError && <Alert type={AlertTypes.error} message={SERVER_ERROR} />}

      <div className={s['removal-request-row-buttons']}>
        <Button
          type="button"
          variant="outline"
          className={clsx(s['removal-request-button'])}
          onClick={hideModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={!isValid}
          loading={isSubmitting}
          className={s['removal-request-button']}
        >
          Submit
        </Button>
      </div>
    </form>
  );
};

export default RemovalRequestForm;
